import { getSchlConfig, LATEST_SCHL_BENCHMARK_VERSION, SCHL_VACANCY_RATE } from '../defaults/schlBenchmarks.js';

export const LISTING_IMPORT_KEYS = [
  'address',
  'city',
  'province',
  'postalCode',
  'askingPrice',
  'purchasePrice',
  'municipalEvaluation',
  'numberOfUnits',
  'structureType',
  'annualRent',
  'parkingRevenue',
  'internetRevenue',
  'storageRevenue',
  'otherRevenue',
  'vacancyRate',
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'electricityHeating',
  'heating',
  'electricity',
  'maintenance',
  'concierge',
  'managementRate',
  'operatingExpenses',
  'otherExpenses',
  'landscaping',
  'snowRemoval',
  'extermination',
  'fireInspection',
  'advertising',
  'legal',
  'accounting',
  'cableInternet',
  'appliances',
  'garbage',
  'washerDryer',
  'hotWater',
  'numFridges',
  'numStoves',
  'numDishwashers',
  'numWashers',
  'numDryers',
  'numHeatPumps',
  'numElevators',
  'marketingDescriptionHtml',
];

const ADVANCED_EXPENSE_KEYS = [
  'heating',
  'electricity',
  'landscaping',
  'snowRemoval',
  'extermination',
  'fireInspection',
  'advertising',
  'legal',
  'accounting',
  'cableInternet',
  'appliances',
  'garbage',
  'washerDryer',
  'hotWater',
];

/** SCHL «other costs` (1% of EGI) already covers these when otherCostRate > 0. */
const SCHL_COVERED_MISC_KEYS = ADVANCED_EXPENSE_KEYS.filter(
  (k) => k !== 'heating' && k !== 'electricity'
);

export const LISTING_PREVIEW_KEYS = [
  'address',
  'city',
  'province',
  'postalCode',
  'numberOfUnits',
  'askingPrice',
  'purchasePrice',
  'annualRent',
  'parkingRevenue',
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'heating',
  'electricity',
  'electricityHeating',
  'operatingExpenses',
  'maintenance',
  'concierge',
  'managementRate',
];

/**
 * @param {Record<string, unknown>} fields
 * @returns {boolean}
 */
export function listingFieldsUseAdvancedExpenses(fields) {
  const f = fields || {};
  return ADVANCED_EXPENSE_KEYS.some((k) => f[k] != null && String(f[k]).trim() !== '');
}

/**
 * Rentalyzer stores maintenance and concierge as annual $ per dwelling.
 * Broker listings often give the building annual total.
 * @param {unknown} amount
 * @param {unknown} units
 * @returns {number | null}
 */
export function toPerUnitAnnualExpense(amount, units) {
  const n = Number(amount);
  const u = Math.round(Number(units));
  if (!Number.isFinite(n) || n <= 0) return Number.isFinite(n) ? n : null;
  if (!Number.isFinite(u) || u < 2) return n;
  if (n <= 1500) return n;
  const per = n / u;
  if (per >= 40 && per <= 4000) return Math.round(per * 100) / 100;
  return n;
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {Record<string, unknown>}
 */
export function convertListingPerUnitExpenses(fields) {
  const next = { ...(fields || {}) };
  const units = Number(next.numberOfUnits);
  for (const key of ['maintenance', 'concierge']) {
    if (next[key] == null || next[key] === '') continue;
    const before = Number(next[key]);
    const after = toPerUnitAnnualExpense(next[key], units);
    if (after != null && Number.isFinite(before) && after !== before) {
      next[key] = String(after);
    }
  }
  return next;
}

function listingEffectiveGrossIncome(fields) {
  const f = fields || {};
  const gross = [
    'annualRent',
    'parkingRevenue',
    'internetRevenue',
    'storageRevenue',
    'otherRevenue',
  ].reduce((sum, key) => sum + (Number(f[key]) || 0), 0);
  const vacancy = Number(f.vacancyRate);
  const vacancyPct = Number.isFinite(vacancy) && vacancy >= 0 ? vacancy : 3;
  return gross * (1 - vacancyPct / 100);
}

function amountsNear(a, b, rel = 0.1, absFloor = 100) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return false;
  return Math.abs(a - b) <= Math.max(absFloor, rel * Math.max(a, b));
}

/**
 * Rentalyzer "Autres dépenses" is the SCHL other-cost bucket (typically 1% of EGI).
 * PMML prints that tag as dollars — or dumps gestion+autres (~6% of EGI) into Autres.
 * Filling the field replaces the live 1% calculation.
 * @param {Record<string, unknown>} fields
 * @returns {boolean}
 */
export function shouldOmitListingOtherExpenses(fields) {
  const listed = Number(fields?.otherExpenses);
  if (!Number.isFinite(listed) || listed <= 0) return false;

  const schl = getSchlConfig({
    province: fields?.province,
    structureType: fields?.structureType || 'woodFrame',
    numberOfUnits: fields?.numberOfUnits,
    schlBenchmarkVersion: fields?.schlBenchmarkVersion || LATEST_SCHL_BENCHMARK_VERSION,
  });
  const otherRate = schl?.otherCostRate || 0;
  if (otherRate > 0) return true;

  const egi = listingEffectiveGrossIncome(fields);
  if (egi <= 0) return false;
  if (amountsNear(listed, egi * 0.01)) return true;
  const mgmt = Number(fields?.managementRate);
  const mgmtRate = Number.isFinite(mgmt) && mgmt > 0 ? mgmt : (schl?.managementRate || 0);
  if (mgmtRate > 0 && amountsNear(listed, (egi * (mgmtRate + 1)) / 100)) return true;
  if (amountsNear(listed, egi * 0.06)) return true;
  return false;
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {Record<string, unknown>}
 */
export function convertListingSchlExpenses(fields, baseProperty = {}) {
  const next = { ...(fields || {}) };
  const schl = getSchlConfig({
    province: next.province,
    structureType: next.structureType || 'woodFrame',
    numberOfUnits: next.numberOfUnits,
    schlBenchmarkVersion: next.schlBenchmarkVersion || LATEST_SCHL_BENCHMARK_VERSION,
  });
  const otherRate = schl?.otherCostRate || 0;
  if (otherRate > 0) {
    for (const key of SCHL_COVERED_MISC_KEYS) {
      next[key] = baseProperty[key] ?? '';
    }
  }
  if (shouldOmitListingOtherExpenses(next)) {
    next.otherExpenses = baseProperty.otherExpenses ?? '';
  }
  return next;
}

/**
 * Merge extracted listing fields onto a property draft. Empty extracted values are skipped.
 * @param {Record<string, unknown>} baseProperty
 * @param {Record<string, unknown>} fields
 * @returns {Record<string, unknown>}
 */
export function applyListingImport(baseProperty, fields) {
  const next = { ...(baseProperty || {}) };
  const src = fields && typeof fields === 'object' ? fields : {};
  for (const key of LISTING_IMPORT_KEYS) {
    const v = src[key];
    if (v == null || v === '') continue;
    if (key === 'marketingDescriptionHtml') {
      const html = String(v).trim().slice(0, 20000);
      if (html) next.marketingDescriptionHtml = html;
      continue;
    }
    if (key === 'vacancyRate') continue;
    if (key === 'structureType' && v !== 'woodFrame' && v !== 'concrete') continue;
    next[key] = typeof v === 'string' || typeof v === 'number' ? v : String(v);
  }
  if (next.askingPrice && (next.purchasePrice === '' || next.purchasePrice == null)) {
    next.purchasePrice = next.askingPrice;
  }
  if (next.purchasePrice && (next.askingPrice === '' || next.askingPrice == null)) {
    next.askingPrice = next.purchasePrice;
  }
  const schlVacancy =
    getSchlConfig({
      province: next.province,
      structureType: next.structureType || 'woodFrame',
      numberOfUnits: next.numberOfUnits,
      schlBenchmarkVersion: next.schlBenchmarkVersion || LATEST_SCHL_BENCHMARK_VERSION,
    })?.vacancyRate ?? SCHL_VACANCY_RATE;
  next.vacancyRate = String(schlVacancy);
  return convertListingSchlExpenses(convertListingPerUnitExpenses(next), baseProperty);
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {Array<{ key: string, value: string }>}
 */
export function listingPreviewRows(fields) {
  const f = fields || {};
  const rows = [];
  for (const key of LISTING_PREVIEW_KEYS) {
    const v = f[key];
    if (v == null || v === '') continue;
    rows.push({ key, value: String(v) });
  }
  return rows;
}
